import { MutationMeta, QueryClient, QueryMeta } from '@tanstack/react-query';

import { profileQueryOptions } from 'api/account';
import { siteDetailsQueryOptions } from 'api/site';

export type OptionsWithMeta = { meta?: MutationMeta | QueryMeta };

export type Link = { rel: string; href?: string; method?: string };

/**
 * Any object the API annotates with `ObjectLinks`. The generated types leave
 * every field optional, hence the loose shape.
 */
export type WithLinks = { links?: readonly { rel?: string }[] | null };

const EMPTY_RELS: Set<string> = new Set();

export function extractLinkRels(queryData: unknown): Set<string> {
  const data = queryData as { result?: { links?: Link[] } } | undefined;
  const links = data?.result?.links ?? [];
  return new Set(links.map(l => l.rel));
}

/** Returns `undefined` for absent links, preserving fallback to account and site links. */
export function extractItemRels(item: WithLinks | null | undefined): Set<string> | undefined {
  const links = item?.links;
  if (!links) return undefined;

  return new Set(links.map(l => l.rel).filter((rel): rel is string => rel != null));
}

export function getOptionsRel(options: OptionsWithMeta): string | undefined {
  return options.meta?.rel as string | undefined;
}

/**
 * Whether `item.links` includes the operation.
 * Returns `undefined` when the item has no `links` array.
 */
export function isOperationDeclaredOnItem(
  item: WithLinks | null | undefined,
  options: OptionsWithMeta
): boolean | undefined {
  const rels = extractItemRels(item);
  if (!rels) return undefined;

  const rel = getOptionsRel(options);
  if (!rel) return true;

  return rels.has(rel);
}

export function getOptionsSiteId(options: OptionsWithMeta): string | number | undefined {
  return options.meta?.siteId as string | number | undefined;
}

export function getOptionsFirstSiteId(
  optionsList: readonly OptionsWithMeta[]
): string | number | undefined {
  for (const options of optionsList) {
    const siteId = getOptionsSiteId(options);
    if (siteId != null) return siteId;
  }
  return undefined;
}

export interface OperationChecker {
  (...optionsList: OptionsWithMeta[]): boolean;
  /** Uses the item's links when present; otherwise falls back to account and site links. */
  for(item: WithLinks | null | undefined, ...optionsList: OptionsWithMeta[]): boolean;
}

export function createOperationChecker(relSets: readonly Set<string>[]): OperationChecker {
  const check = (itemRels: Set<string> | undefined, optionsList: readonly OptionsWithMeta[]) =>
    optionsList.some(options => {
      const rel = getOptionsRel(options);
      if (!rel) return true;

      return itemRels ? itemRels.has(rel) : relSets.some(set => set.has(rel));
    });

  const checker = ((...optionsList: OptionsWithMeta[]) =>
    check(undefined, optionsList)) as OperationChecker;

  checker.for = (item, ...optionsList) => check(extractItemRels(item), optionsList);

  return checker;
}

function getAccountLinksFromCache(queryClient: QueryClient): Set<string> {
  return extractLinkRels(queryClient.getQueryData(profileQueryOptions().queryKey));
}

function getSiteLinksFromCache(queryClient: QueryClient, siteId: string | number): Set<string> {
  return extractLinkRels(
    queryClient.getQueryData(siteDetailsQueryOptions(String(siteId)).queryKey)
  );
}

export function hasAccountDataInCache(queryClient: QueryClient): boolean {
  return queryClient.getQueryData(profileQueryOptions().queryKey) !== undefined;
}

export function hasSiteDataInCache(queryClient: QueryClient, siteId: string | number): boolean {
  return queryClient.getQueryData(siteDetailsQueryOptions(String(siteId)).queryKey) !== undefined;
}

export function isOperationAvailableInCache(
  queryClient: QueryClient,
  optionsList: readonly OptionsWithMeta[],
  siteId?: string | number
): boolean {
  const checker = createOperationChecker([
    getAccountLinksFromCache(queryClient),
    siteId != null ? getSiteLinksFromCache(queryClient, siteId) : EMPTY_RELS,
  ]);

  return checker(...optionsList);
}
